(() => {
  if (window.GiantFinancingModuleV2) {
    window.GiantFinancingModuleV2.initAll(document);
    return;
  }

  const SELECTOR = '[data-giant-financing]';
  const MP_SDK_URL = 'https://sdk.mercadopago.com/js/v2';
  const mpInstances = new Map();
  let mpSdkPromise;

  const currencyFormatter = new Intl.NumberFormat('es-AR', {
    style: 'currency',
    currency: 'ARS',
    minimumFractionDigits: 0,
    maximumFractionDigits: 2
  });

  const percentageFormatter = new Intl.NumberFormat('es-AR', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2
  });

  const formatMoneyFromCents = (cents) => currencyFormatter.format((Number(cents) || 0) / 100);
  const formatMoney = (amount) => currencyFormatter.format(Number(amount) || 0);

  const parseNumber = (value) => {
    const normalized = String(value ?? '').trim().replace(',', '.');
    const number = Number.parseFloat(normalized);
    return Number.isFinite(number) ? number : null;
  };

  const parsePlans = (gateway) => {
    const mode = gateway.mode || 'surcharge';

    return String(gateway.plans || '')
      .split(/\r?\n/)
      .map((line) => line.trim())
      .filter((line) => line && !line.startsWith('#'))
      .map((line) => {
        const [installmentsRaw, valueRaw] = line.split('|').map((part) => part.trim());
        const installments = Number.parseInt(installmentsRaw, 10);
        const value = parseNumber(valueRaw);

        if (!Number.isInteger(installments) || installments < 1 || value === null || value < 0) {
          return null;
        }

        return { installments, value, mode };
      })
      .filter(Boolean)
      .sort((a, b) => a.installments - b.installments);
  };

  const calculatePlan = (priceCents, plan) => {
    let totalCents;
    let surchargePercent;

    if (plan.mode === 'multiplier') {
      totalCents = Math.round(priceCents * plan.value);
      surchargePercent = (plan.value - 1) * 100;
    } else {
      surchargePercent = plan.value;
      totalCents = Math.round(priceCents * (1 + (plan.value / 100)));
    }

    return {
      ...plan,
      totalCents,
      installmentCents: Math.round(totalCents / plan.installments),
      surchargePercent
    };
  };

  const loadMercadoPagoSdk = () => {
    if (window.MercadoPago) return Promise.resolve();
    if (mpSdkPromise) return mpSdkPromise;

    mpSdkPromise = new Promise((resolve, reject) => {
      const existing = document.querySelector(`script[src="${MP_SDK_URL}"]`);
      if (existing) {
        existing.addEventListener('load', resolve, { once: true });
        existing.addEventListener('error', () => reject(new Error('No se pudo cargar MercadoPago.js')), { once: true });
        return;
      }

      const script = document.createElement('script');
      script.src = MP_SDK_URL;
      script.async = true;
      script.onload = resolve;
      script.onerror = () => reject(new Error('No se pudo cargar MercadoPago.js'));
      document.head.appendChild(script);
    });

    return mpSdkPromise;
  };

  const getMercadoPago = async (publicKey) => {
    await loadMercadoPagoSdk();
    if (!window.MercadoPago) throw new Error('MercadoPago.js no está disponible');

    if (!mpInstances.has(publicKey)) {
      mpInstances.set(publicKey, new window.MercadoPago(publicKey, { locale: 'es-AR' }));
    }

    return mpInstances.get(publicKey);
  };

  const parseTaxes = (labels = []) => {
    const joined = Array.isArray(labels) ? labels.join('|') : String(labels || '');
    const cftMatch = joined.match(/CFT_([^|]+)/i);
    const teaMatch = joined.match(/TEA_([^|]+)/i);

    return {
      cft: cftMatch ? cftMatch[1] : null,
      tea: teaMatch ? teaMatch[1] : null
    };
  };

  const numericPercent = (value) => {
    if (!value) return null;
    const parsed = Number.parseFloat(String(value).replace('%', '').replace(',', '.'));
    return Number.isFinite(parsed) ? parsed : null;
  };

  const isInterestFree = (payerCost, cashAmount) => {
    const taxes = parseTaxes(payerCost.labels);
    const cft = numericPercent(taxes.cft);
    const tea = numericPercent(taxes.tea);
    const total = Number(payerCost.total_amount);
    const sameTotal = Number.isFinite(total) && Math.abs(total - cashAmount) < 0.02;

    return payerCost.installments > 1 && sameTotal && cft === 0 && tea === 0;
  };

  const setStatus = (group, message, type = '') => {
    const status = group.querySelector('[data-mp-status]');
    if (!status) return;
    status.textContent = message || '';
    status.dataset.type = type;
    status.hidden = !message;
  };

  const createOption = (value, text) => {
    const option = document.createElement('option');
    option.value = String(value ?? '');
    option.textContent = text || String(value ?? '');
    return option;
  };

  const resetSelect = (select, placeholder) => {
    select.innerHTML = '';
    select.appendChild(createOption('', placeholder));
    select.disabled = true;
  };

  const selectPaymentMethod = (methods, id) => methods.find((method) => String(method.id) === String(id)) || methods[0];

  const selectInstallmentGroups = (groups, paymentMethodId, issuerId) => {
    if (!Array.isArray(groups) || !groups.length) return [];

    let candidates = groups;
    const byMethod = groups.filter((group) => !group.payment_method_id || String(group.payment_method_id) === String(paymentMethodId));
    if (byMethod.length) candidates = byMethod;

    if (issuerId) {
      const byIssuer = candidates.filter((group) => {
        const groupIssuerId = String(group.issuer?.id ?? group.issuer_id ?? '');
        return !groupIssuerId || groupIssuerId === String(issuerId);
      });
      if (byIssuer.length) candidates = byIssuer;
    }

    return candidates;
  };

  const isPlanAllowedForAmount = (payerCost, cashAmount) => {
    const min = Number(payerCost.min_allowed_amount);
    const max = Number(payerCost.max_allowed_amount);
    const tolerance = 0.01;

    if (Number.isFinite(min) && min > 0 && cashAmount + tolerance < min) return false;
    if (Number.isFinite(max) && max > 0 && cashAmount - tolerance > max) return false;
    return true;
  };

  const collectEligiblePayerCosts = (groups, paymentMethodId, issuerId, cashAmount) => {
    const candidates = selectInstallmentGroups(groups, paymentMethodId, issuerId);
    const eligible = candidates
      .flatMap((group) => Array.isArray(group?.payer_costs) ? group.payer_costs : [])
      .filter((payerCost) => isPlanAllowedForAmount(payerCost, cashAmount))
      // Giant Argentina muestra planes de hasta 18 cuotas.
      // Mercado Pago puede devolver alternativas adicionales para ciertos BIN;
      // no las mostramos aunque estén presentes en la respuesta del SDK.
      .filter((payerCost) => Number(payerCost.installments) <= 18);

    // Mercado Pago puede devolver más de una alternativa para la misma cantidad
    // de cuotas. Para la ficha mostramos la opción válida de menor total financiado.
    const bestByInstallments = new Map();
    eligible.forEach((payerCost) => {
      const key = Number(payerCost.installments);
      if (!Number.isFinite(key)) return;

      const current = bestByInstallments.get(key);
      const total = Number(payerCost.total_amount);
      const currentTotal = Number(current?.total_amount);

      if (!current || !Number.isFinite(currentTotal) || (Number.isFinite(total) && total < currentTotal)) {
        bestByInstallments.set(key, payerCost);
      }
    });

    return Array.from(bestByInstallments.values())
      .sort((a, b) => Number(a.installments) - Number(b.installments));
  };

  const renderApiPlans = (module, gateway, groupEl, payerCosts, cashAmount) => {
    const plansEl = groupEl.querySelector('[data-mp-plans]');
    plansEl.innerHTML = '';

    if (!payerCosts.length) {
      setStatus(groupEl, 'No encontramos cuotas disponibles para esta tarjeta y este importe.', 'error');
      return;
    }

    payerCosts.forEach((payerCost) => {
      const row = document.createElement('div');
      row.className = 'giant-financing__plan giant-financing__plan--api';

      const main = document.createElement('div');
      main.className = 'giant-financing__plan-main';

      const amount = document.createElement('strong');
      amount.textContent = `${payerCost.installments} ${payerCost.installments === 1 ? 'cuota' : 'cuotas'} de ${formatMoney(payerCost.installment_amount)}`;

      const detail = document.createElement('span');
      if (isInterestFree(payerCost, cashAmount)) {
        detail.textContent = 'Sin interés';
      } else if (payerCost.installments === 1) {
        detail.textContent = 'Pago en 1 cuota';
      } else {
        const rate = Number(payerCost.installment_rate);
        detail.textContent = Number.isFinite(rate) && rate > 0
          ? `Tasa de financiación ${percentageFormatter.format(rate)}%`
          : 'Financiación según tarjeta';
      }

      main.append(amount, detail);

      const financial = document.createElement('div');
      financial.className = 'giant-financing__financial-data';

      const total = document.createElement('div');
      total.className = 'giant-financing__plan-total';
      total.innerHTML = `<span>Total financiado</span><strong>${formatMoney(payerCost.total_amount)}</strong>`;
      financial.appendChild(total);

      const taxes = parseTaxes(payerCost.labels);
      if (payerCost.installments > 1 && (taxes.cft || taxes.tea)) {
        const taxWrap = document.createElement('div');
        taxWrap.className = 'giant-financing__taxes';

        if (taxes.tea) {
          const tea = document.createElement('span');
          tea.className = 'giant-financing__tea';
          tea.textContent = `TEA ${taxes.tea}`;
          taxWrap.appendChild(tea);
        }

        if (taxes.cft) {
          const cft = document.createElement('strong');
          cft.className = 'giant-financing__cft';
          cft.textContent = `CFT ${taxes.cft}`;
          taxWrap.appendChild(cft);
        }

        financial.appendChild(taxWrap);
      }

      row.append(main, financial);
      plansEl.appendChild(row);
    });

    const featured = payerCosts
      .filter((cost) => Number(cost.installments) > 1)
      .sort((a, b) => Number(b.installments) - Number(a.installments))[0] || payerCosts[payerCosts.length - 1];

    if (featured) {
      const summaryMain = module.querySelector('[data-financing-summary-main]');
      const summaryGateway = module.querySelector('[data-financing-summary-gateway]');
      summaryMain.textContent = isInterestFree(featured, cashAmount)
        ? `${featured.installments} cuotas sin interés de ${formatMoney(featured.installment_amount)}`
        : `${featured.installments} cuotas de ${formatMoney(featured.installment_amount)}`;
      summaryGateway.textContent = `con ${gateway.name}`;
    }

    setStatus(groupEl, 'Opciones actualizadas según la tarjeta ingresada.', 'success');
  };

  const consultMercadoPago = async (module, gateway, groupEl) => {
    const binInput = groupEl.querySelector('[data-mp-bin]');
    const cardValue = groupEl.querySelector('[data-mp-card]');
    const issuerValue = groupEl.querySelector('[data-mp-issuer]');
    const plansEl = groupEl.querySelector('[data-mp-plans]');
    const bin = String(binInput.value || '').replace(/\D/g, '').slice(0, 8);

    binInput.value = bin;
    plansEl.innerHTML = '';
    cardValue.textContent = 'Detectando…';
    issuerValue.textContent = 'Detectando…';

    groupEl._mpRequestSeq = (groupEl._mpRequestSeq || 0) + 1;
    const requestSeq = groupEl._mpRequestSeq;
    const isCurrentRequest = () => requestSeq === groupEl._mpRequestSeq;

    if (bin.length !== 8) {
      setStatus(groupEl, 'Ingresá los primeros 8 dígitos de la tarjeta.', 'error');
      return;
    }

    if (groupEl.dataset.lastBin === bin && plansEl.children.length) return;

    const priceCents = Number(module.dataset.price);
    if (!Number.isFinite(priceCents) || priceCents <= 0) return;
    const cashAmount = priceCents / 100;

    setStatus(groupEl, 'Consultando condiciones vigentes en Mercado Pago…');

    try {
      const mp = await getMercadoPago(gateway.publicKey);
      if (!isCurrentRequest()) return;

      const paymentMethodsResponse = await mp.getPaymentMethods({ bin });
      if (!isCurrentRequest()) return;

      const allMethods = Array.isArray(paymentMethodsResponse?.results) ? paymentMethodsResponse.results : [];
      const methods = allMethods.filter((method) => method.payment_type_id === 'credit_card');
      const usableMethods = methods.length ? methods : allMethods;

      if (!usableMethods.length) throw new Error('No se encontró un medio de pago para ese BIN.');

      const method = usableMethods[0];
      cardValue.textContent = method.name || method.id || 'Tarjeta detectada';

      let issuers = [];
      try {
        issuers = await mp.getIssuers({ paymentMethodId: method.id, bin });
      } catch (error) {
        issuers = [];
      }
      if (!isCurrentRequest()) return;

      if (!Array.isArray(issuers) || !issuers.length) {
        issuers = method.issuer?.id ? [method.issuer] : [];
      }

      const issuer = issuers[0] || null;
      issuerValue.textContent = issuer?.name || 'Determinado por Mercado Pago';

      const installmentParams = {
        amount: String(cashAmount),
        bin,
        locale: 'es-AR'
      };
      if (method.processing_mode === 'aggregator' || method.processing_mode === 'gateway') {
        installmentParams.processingMode = method.processing_mode;
      }

      const installments = await mp.getInstallments(installmentParams);
      if (!isCurrentRequest()) return;

      const payerCosts = collectEligiblePayerCosts(installments, method.id, issuer?.id, cashAmount);
      renderApiPlans(module, gateway, groupEl, payerCosts, cashAmount);
      groupEl.dataset.lastBin = bin;
    } catch (error) {
      if (!isCurrentRequest()) return;
      console.warn('Giant financing / Mercado Pago:', error);
      cardValue.textContent = 'No identificado';
      issuerValue.textContent = 'No identificado';
      setStatus(groupEl, 'No pudimos consultar las cuotas en este momento. Verificá los dígitos e intentá nuevamente.', 'error');
    }
  };

  const buildApiGateway = (module, gateway) => {
    const group = document.createElement('section');
    group.className = 'giant-financing__gateway giant-financing__gateway--api';
    group.dataset.gatewayId = gateway.id;

    const title = document.createElement('h3');
    title.textContent = gateway.name;
    group.appendChild(title);

    const intro = document.createElement('p');
    intro.className = 'giant-financing__gateway-intro';
    intro.textContent = 'Ingresá los primeros 8 dígitos de tu tarjeta. Al completar el octavo dígito, consultaremos automáticamente las cuotas vigentes.';
    group.appendChild(intro);

    const lookup = document.createElement('div');
    lookup.className = 'giant-financing__lookup giant-financing__lookup--automatic';
    lookup.innerHTML = `
      <label class="giant-financing__field giant-financing__field--bin">
        <span>Primeros 8 dígitos</span>
        <input type="text" inputmode="numeric" autocomplete="off" maxlength="8" pattern="[0-9]{8}" placeholder="12345678" data-mp-bin aria-describedby="mp-bin-help-${gateway.id}">
      </label>
      <p class="giant-financing__field-help" id="mp-bin-help-${gateway.id}">No ingreses el número completo de la tarjeta.</p>
    `;
    group.appendChild(lookup);

    const detected = document.createElement('div');
    detected.className = 'giant-financing__detected';
    detected.innerHTML = `
      <div class="giant-financing__detected-item">
        <span class="giant-financing__detected-label">Tarjeta</span>
        <div class="giant-financing__detected-value" data-mp-card>Se detecta con los 8 dígitos</div>
      </div>
      <div class="giant-financing__detected-item">
        <span class="giant-financing__detected-label">Banco emisor</span>
        <div class="giant-financing__detected-value" data-mp-issuer>Se detecta con los 8 dígitos</div>
      </div>
    `;
    group.appendChild(detected);

    const status = document.createElement('p');
    status.className = 'giant-financing__status';
    status.dataset.mpStatus = '';
    status.setAttribute('aria-live', 'polite');
    status.hidden = true;
    group.appendChild(status);

    const plans = document.createElement('div');
    plans.className = 'giant-financing__plans';
    plans.dataset.mpPlans = '';
    group.appendChild(plans);

    const binInput = lookup.querySelector('[data-mp-bin]');
    let autoLookupTimer;
    binInput.addEventListener('input', () => {
      const bin = String(binInput.value || '').replace(/\D/g, '').slice(0, 8);
      binInput.value = bin;
      clearTimeout(autoLookupTimer);

      if (bin.length < 8) {
        group._mpRequestSeq = (group._mpRequestSeq || 0) + 1;
        delete group.dataset.lastBin;
        plans.innerHTML = '';
        detected.querySelector('[data-mp-card]').textContent = 'Se detecta con los 8 dígitos';
        detected.querySelector('[data-mp-issuer]').textContent = 'Se detecta con los 8 dígitos';
        setStatus(group, '');
        return;
      }

      if (group.dataset.lastBin === bin) return;
      autoLookupTimer = window.setTimeout(() => consultMercadoPago(module, gateway, group), 120);
    });

    if (gateway.note) {
      const note = document.createElement('p');
      note.className = 'giant-financing__gateway-note';
      note.textContent = gateway.note;
      group.appendChild(note);
    }

    return group;
  };

  const buildManualGateway = (gateway, priceCents) => {
    const group = document.createElement('section');
    group.className = 'giant-financing__gateway';

    const title = document.createElement('h3');
    title.textContent = gateway.name;
    group.appendChild(title);

    const plans = document.createElement('div');
    plans.className = 'giant-financing__plans';

    gateway.parsedPlans.forEach((plan) => {
      const calculated = calculatePlan(priceCents, plan);
      const row = document.createElement('div');
      row.className = 'giant-financing__plan';

      const left = document.createElement('div');
      left.className = 'giant-financing__plan-main';

      const amount = document.createElement('strong');
      amount.textContent = `${calculated.installments} ${calculated.installments === 1 ? 'cuota' : 'cuotas'} de ${formatMoneyFromCents(calculated.installmentCents)}`;

      const rate = document.createElement('span');
      rate.textContent = Math.abs(calculated.surchargePercent) < 0.0001
        ? 'Sin interés'
        : `Recargo ${percentageFormatter.format(calculated.surchargePercent)}%`;

      left.append(amount, rate);

      const total = document.createElement('div');
      total.className = 'giant-financing__plan-total';
      total.innerHTML = `<span>Total</span><strong>${formatMoneyFromCents(calculated.totalCents)}</strong>`;

      row.append(left, total);
      plans.appendChild(row);
    });

    group.appendChild(plans);

    if (gateway.note) {
      const note = document.createElement('p');
      note.className = 'giant-financing__gateway-note';
      note.textContent = gateway.note;
      group.appendChild(note);
    }

    return group;
  };

  const getManualFeatured = (gateways, priceCents) => {
    const allPlans = gateways
      .filter((gateway) => gateway.source === 'manual')
      .flatMap((gateway, gatewayIndex) => (
        gateway.parsedPlans.map((plan) => ({
          ...calculatePlan(priceCents, plan),
          gatewayName: gateway.name,
          gatewayIndex
        }))
      ));

    if (!allPlans.length) return null;

    const interestFree = allPlans
      .filter((plan) => Math.abs(plan.surchargePercent) < 0.0001)
      .sort((a, b) => b.installments - a.installments || a.gatewayIndex - b.gatewayIndex);

    if (interestFree.length) return interestFree[0];

    return allPlans.sort((a, b) => b.installments - a.installments || a.surchargePercent - b.surchargePercent)[0];
  };

  const render = (module, priceCents, { variantChanged = false } = {}) => {
    if (!Number.isFinite(priceCents) || priceCents <= 0) {
      module.hidden = true;
      return;
    }

    module.hidden = false;
    module.dataset.price = String(priceCents);

    const gateways = module.financingGateways;
    const summaryMain = module.querySelector('[data-financing-summary-main]');
    const summaryGateway = module.querySelector('[data-financing-summary-gateway]');
    const cashPrice = module.querySelector('[data-financing-cash-price]');
    const gatewayContainer = module.querySelector('[data-financing-gateways]');
    const apiGateway = gateways.find((gateway) => gateway.source === 'mercadopago_api');
    const manualFeatured = getManualFeatured(gateways, priceCents);

    cashPrice.textContent = formatMoneyFromCents(priceCents);
    gatewayContainer.innerHTML = '';

    if (apiGateway) {
      summaryMain.textContent = 'Consultá las cuotas disponibles';
      summaryGateway.textContent = `con ${apiGateway.name}`;
    } else if (manualFeatured) {
      summaryMain.textContent = Math.abs(manualFeatured.surchargePercent) < 0.0001
        ? `${manualFeatured.installments} cuotas sin interés de ${formatMoneyFromCents(manualFeatured.installmentCents)}`
        : `${manualFeatured.installments} cuotas de ${formatMoneyFromCents(manualFeatured.installmentCents)}`;
      summaryGateway.textContent = `con ${manualFeatured.gatewayName}`;
    }

    gateways.forEach((gateway) => {
      if (gateway.source === 'mercadopago_api') {
        gatewayContainer.appendChild(buildApiGateway(module, gateway));
      } else if (gateway.parsedPlans.length) {
        gatewayContainer.appendChild(buildManualGateway(gateway, priceCents));
      }
    });

    if (variantChanged && apiGateway) {
      const status = gatewayContainer.querySelector('[data-mp-status]');
      if (status) {
        status.textContent = 'El precio cambió. Volvé a consultar las cuotas para esta variante.';
        status.dataset.type = '';
        status.hidden = false;
      }
    }
  };

  const closeModal = (module) => {
    const overlay = module.querySelector('[data-financing-overlay]');
    if (!overlay || overlay.hidden) return;

    overlay.hidden = true;
    document.documentElement.classList.remove('giant-financing-open');
    module.querySelector('[data-financing-open]')?.focus();
  };

  const openModal = (module) => {
    const overlay = module.querySelector('[data-financing-overlay]');
    if (!overlay) return;

    overlay.hidden = false;
    document.documentElement.classList.add('giant-financing-open');
    module.querySelector('.giant-financing__close')?.focus();
  };

  const initModule = (module) => {
    if (module.dataset.financingReady === 'true') return;

    const configEl = module.querySelector('[data-financing-config]');
    if (!configEl) return;

    let gateways;
    try {
      gateways = JSON.parse(configEl.textContent || '[]');
    } catch (error) {
      console.warn('Giant financing: configuración inválida.', error);
      module.hidden = true;
      return;
    }

    module.financingGateways = gateways
      .map((gateway) => ({
        ...gateway,
        source: gateway.source || 'manual',
        parsedPlans: gateway.source === 'mercadopago_api' ? [] : parsePlans(gateway)
      }))
      .filter((gateway) => gateway.name && (
        (gateway.source === 'mercadopago_api' && gateway.publicKey)
        || (gateway.source === 'manual' && gateway.parsedPlans.length)
      ));

    if (!module.financingGateways.length) {
      module.hidden = true;
      return;
    }

    module.querySelector('[data-financing-open]')?.addEventListener('click', () => openModal(module));
    module.querySelectorAll('[data-financing-close]').forEach((el) => {
      el.addEventListener('click', () => closeModal(module));
    });

    module.addEventListener('keydown', (event) => {
      if (event.key === 'Escape') closeModal(module);
    });

    module.dataset.financingReady = 'true';
    render(module, Number(module.dataset.price));
  };

  const initAll = (root = document) => {
    root.querySelectorAll(SELECTOR).forEach(initModule);
  };

  document.addEventListener('on:variant:change', (event) => {
    const productSection = event.target.closest('.js-product');
    if (!productSection) return;

    const variant = event.detail?.variant;

    productSection.querySelectorAll(SELECTOR).forEach((module) => {
      initModule(module);
      if (!variant) {
        module.hidden = true;
        return;
      }
      render(module, Number(variant.price), { variantChanged: true });
    });
  });

  window.GiantFinancingModuleV2 = { initAll };

  document.addEventListener('shopify:section:load', (event) => initAll(event.target));

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initAll());
  } else {
    initAll();
  }
})();